import type { ClientResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@trellis/roots/command'
import { Popover, PopoverContent, PopoverTrigger } from '@trellis/roots/popover'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'

import { AddClientDialog } from '@/components/add-client-dialog'
import {
  useClientQuery,
  useClientsQuery,
  useDebouncedSearch,
  useDisclosure,
} from '@/hooks'
import { cn } from '@/lib/utils'

const CLIENT_PAGE_LIMIT = 7

interface ClientComboboxProps {
  id?: string
  value: string
  onChange: (client: ClientResponseDto) => void
}

export function ClientCombobox({ id, value, onChange }: ClientComboboxProps) {
  const [open, setOpen] = useState(false)
  const createDialog = useDisclosure()
  const [createDialogKey, setCreateDialogKey] = useState(0)
  const { inputValue, debouncedValue, setInputValue } = useDebouncedSearch()

  const { data, hasNextPage, isFetchingNextPage, goToNextPage, setPageIndex } =
    useClientsQuery({ search: debouncedValue, limit: CLIENT_PAGE_LIMIT })
  const clients = data?.pages.flatMap((page) => page.data ?? []) ?? []

  // useClientsQuery's pageIndex tracks the "current page" for the paged table view;
  // this combobox accumulates pages instead, so pageIndex must be reset on each new
  // search or hasNextPage (which is computed from pageIndex) reports stale results.
  useEffect(() => {
    setPageIndex(0)
  }, [debouncedValue, setPageIndex])

  // The search-scoped result list won't contain the selected client once the user
  // types a different search, so fall back to fetching it directly by id.
  const clientInList = clients.find((client) => client.id === value) ?? null
  const selectedClientQuery = useClientQuery(value, !clientInList)
  const selectedClient = clientInList ?? selectedClientQuery.data?.data ?? null

  const trimmedQuery = inputValue.trim()

  function handleSelect(client: ClientResponseDto) {
    onChange(client)
    setOpen(false)
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between rounded-md font-normal',
              !selectedClient && 'text-muted-foreground',
            )}
          >
            {selectedClient?.name ?? 'Select a client'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              className="text-base md:text-sm"
              placeholder="Search clients…"
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              <CommandGroup>
                {clients.map((client) => (
                  <CommandItem
                    key={client.id}
                    value={client.id}
                    onSelect={() => handleSelect(client)}
                  >
                    {value === client.id && <Check className="mr-2 h-4 w-4" />}
                    {client.name}
                  </CommandItem>
                ))}
              </CommandGroup>
              {hasNextPage && (
                <CommandGroup>
                  <CommandItem
                    value="show-more-clients"
                    disabled={isFetchingNextPage}
                    onSelect={() => goToNextPage()}
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Show more'}
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandSeparator alwaysRender />
              <CommandGroup>
                <CommandItem
                  value={
                    trimmedQuery.length > 0
                      ? `create-${trimmedQuery}`
                      : 'create-new-client'
                  }
                  onSelect={() => {
                    setOpen(false)
                    setCreateDialogKey((key) => key + 1)
                    createDialog.open(true)
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {trimmedQuery.length > 0 ? (
                    <>Create &quot;{trimmedQuery}&quot;</>
                  ) : (
                    'Create new client'
                  )}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AddClientDialog
        key={createDialogKey}
        open={createDialog.isOpen}
        onOpenChange={createDialog.onOpenChange}
        defaultName={trimmedQuery}
        onCreated={handleSelect}
      />
    </>
  )
}
